import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCustomerScope } from '../contexts/AccountContext';
import type { PropertyDirectoryEntry } from '../lib/propertyDirectory';
import {
  fetchDirectoryByCodes,
  resolveCoordinates,
  type LocationAddress,
  type LocationHierarchy,
} from './useLocations';

export interface LocationAsset {
  id: string;
  location_id: string;
  deployment_id: string | null;
  asset_id_label: string | null;
  device_name: string | null;
  make: string | null;
  model: string | null;
  serial_number: string | null;
  ip_address: string | null;
  firmware_version: string | null;
  warranty_expiry: string | null;
  status: string | null;
  updated_at: string;
}

export interface LocationDeploymentDetail {
  id: string;
  location_id: string;
  solution_type: string;
  vendor: string | null;
  status: string | null;
  commissioned_date: string | null;
  sla_response_hours: number | null;
  last_health_check: string | null;
  created_at: string;
  updated_at: string;
  assets: LocationAsset[];
}

export interface LocationDetail {
  id: string;
  customer_id: string;
  name: string;
  type: string;
  address: LocationAddress | null;
  hierarchy: LocationHierarchy | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  timezone: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  deployments: LocationDeploymentDetail[];
  directory: PropertyDirectoryEntry | null;
  coordinates: { lat: number; lng: number } | null;
}

/**
 * One location, scoped to the current account selection: in single-account
 * mode a location from another linked account is "not found".
 */
export function useLocation(id: string | undefined) {
  const scope = useCustomerScope();

  return useQuery({
    queryKey: ['location', scope.key, id],
    enabled: Boolean(id) && scope.ready && scope.ids.length > 0,
    queryFn: async (): Promise<LocationDetail | null> => {
      const { data, error } = await supabase
        .from('locations')
        .select(`
          id, customer_id, name, type, address, hierarchy,
          contact_name, contact_phone, contact_email, timezone,
          status, created_at, updated_at, deleted_at,
          deployments (
            id, location_id, solution_type, vendor, status,
            commissioned_date, sla_response_hours,
            last_health_check, created_at, updated_at,
            assets (
              id, location_id, deployment_id, asset_id_label,
              device_name, make, model, serial_number, ip_address,
              firmware_version, warranty_expiry, status, updated_at
            )
          )
        `)
        .eq('id', id!)
        .in('customer_id', scope.ids)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;

      const row = data as unknown as Omit<LocationDetail, 'directory' | 'coordinates'>;
      const code = row.hierarchy?.property_code;
      const directory = code ? (await fetchDirectoryByCodes([code])).get(code) ?? null : null;
      return {
        ...row,
        deployments: (row.deployments ?? []).map((d) => ({ ...d, assets: d.assets ?? [] })),
        directory,
        coordinates: resolveCoordinates(row.address, directory),
      };
    },
    staleTime: 2 * 60 * 1000,
  });
}
